// ============================================================================
// JobImportForm ViewModel
// ============================================================================

import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import type { FeatureCollection } from "geojson";
import type { Job, Contact } from "../../../../models";
import type { ColumnContext } from "../../ImportTable/ImportTable/columns/standard-column-types/ColumnContext";
import { isFieldVisible, validateJobs, ValidationContext } from "../../../../utilities/jobValidation";
import type { ImportTableColumn } from "../../../../utilities/types";
import { findDeal } from "../../ImportTable/ImportTable/ImportTable.helpers";
import { updateJobFromDeal } from "../../ImportTable/ImportTable/columns/Deal.column/Deal.column.helpers";
import { useDropdownOptions } from "../useDropdownOptions";
import { JobImportFormProps, JobImportFormViewModel } from "./JobImportForm.types";
import { extractJobFieldsFromBoundary } from "../extractJobFieldsFromBoundary";

/**
 * ViewModel hook for JobImportForm component
 */
export function useJobImportFormViewModel(props: JobImportFormProps): JobImportFormViewModel {
	const { job, formContext, onSaveAndValidate, onRegisterSave, onValidationErrorsChange, propertyMappings } = props;

	const {
		company,
		allDeals = [],
		salespersons,
		branches,
		growers,
		user,
		lastCropOptions,
		nextCropOptions,
	} = formContext;

	// React Hook Form
	const { watch, setValue, reset } = useForm<Job>({
		defaultValues: job,
	});

	// Reset form when job changes, and auto-populate deal fields if a deal is selected
	useEffect(() => {
		// Check if job has a deal selected but deal-based fields are empty/zero
		const dealId = job.dealIds?.[0];
		if (dealId && dealId !== "custom") {
			const deal = allDeals.find((d) => d.id === dealId);
			if (deal) {
				// If deal fields are empty/zero, populate them from the deal
				const needsDensity = !job.importDensity || job.importDensity === 0;
				const needsDepth = !job.importDepth || job.importDepth === 0;
				const needsTestPackage = !job.externalTestPackageId;
				const needsFrequency = job.externalFrequencyOfMicros === undefined;

				if (needsDensity || needsDepth || needsTestPackage || needsFrequency) {
					// Create a copy and populate from deal
					const populatedJob = { ...job };
					updateJobFromDeal(populatedJob, deal);
					reset(populatedJob);
					return;
				}
			}
		}
		reset(job);
	}, [job.id, reset, allDeals]);

	// Watch form values
	const currentJob = watch() as Job;

	// State for new salespersons (needed for SalespersonColumnMobile)
	const [newSalespersons, setNewSalespersons] = useState<Contact[]>([]);
	const [readySelectionState, setReadySelectionState] = useState<Record<string, boolean>>({});
	const [localValidationErrors, setLocalValidationErrors] = useState<Record<string, string>>({});

	// Show More / Show Less toggle for secondary fields
	const [showMoreFields, setShowMoreFields] = useState(false);
	const toggleShowMoreFields = useCallback(() => setShowMoreFields((prev) => !prev), []);

	// Validation context - defined early so it can be used in the initial validation
	const validationContext: ValidationContext = useMemo(
		() => ({
			company: company ?? undefined,
			deal: undefined, // Will be set based on current form state
			allDeals,
			job: currentJob,
			user: user ?? undefined,
			hasPointsFile: !!(currentJob.files?.pointsSHP && (currentJob.files.pointsSHP as File[]).length > 0),
			isCreatingNewSalesperson: currentJob.salespersonIds?.[0] === "CREATE_NEW_SALESPERSON",
		}),
		[company, allDeals, currentJob, user],
	);

	// Refs to hold latest values so handleSave callback can be stable
	// Without this, handleSave changes on every render causing infinite loop in onRegisterSave effect
	const currentJobRef = useRef<Job>(currentJob);
	const validationContextRef = useRef<ValidationContext>(validationContext);
	currentJobRef.current = currentJob;
	validationContextRef.current = validationContext;

	// Run full validation on mount and when job changes to show errors immediately
	// Uses a timeout to ensure validation runs after deal fields are auto-populated
	useEffect(() => {
		// Use a small delay to ensure the form has been reset with auto-populated deal fields
		const timer = setTimeout(() => {
			// Get the current form values (which may have been populated from deal)
			const formValues = watch();
			const errors = validateJobs([formValues as Job], {
				...validationContextRef.current,
				job: formValues as Job,
			});
			setLocalValidationErrors(errors);
		}, 0);
		return () => clearTimeout(timer);
	}, [job.id, watch]); // Only re-run when job changes (navigating to different issue)

	// Report validation error state to parent whenever it changes
	useEffect(() => {
		onValidationErrorsChange?.(Object.keys(localValidationErrors).length > 0);
	}, [localValidationErrors, onValidationErrorsChange]);

	// Handle save - re-validates and notifies parent
	// Uses refs so callback reference stays stable (prevents infinite loop in onRegisterSave effect)
	const handleSave = useCallback(() => {
		const jobToSave = currentJobRef.current;
		const context = validationContextRef.current;
		const errors = validateJobs([jobToSave], {
			...context,
			job: jobToSave,
		});
		setLocalValidationErrors(errors);
		const hasErrors = Object.keys(errors).length > 0;
		onSaveAndValidate(jobToSave, hasErrors);
	}, [onSaveAndValidate]);

	// Register save handler with parent for bottom bar button
	useEffect(() => {
		if (onRegisterSave) {
			onRegisterSave(handleSave);
		}
	}, [onRegisterSave, handleSave]);

	// Dropdown options from deals (shared hook)
	const { gridSizes, depths, labPackages, micros } = useDropdownOptions({ allDeals, job });

	// Helper functions for column context
	const isNewSalesperson = (id: string): boolean => id.startsWith("temp-");
	const findNewSalesperson = (id: string): Contact | undefined => newSalespersons.find((sp) => sp.id === id);

	// Column context for mobile columns
	const columnContext: ColumnContext = useMemo(() => {
		const setJobs = (updater: Job[] | ((prev: Job[]) => Job[])) => {
			const jobs = [currentJob];
			const updated = typeof updater === "function" ? updater(jobs) : updater;
			const updatedJob = updated[0];

			if (updatedJob) {
				// Update each changed field via react-hook-form (local state only)
				// Changes are NOT committed to parent until "Save Changes" is clicked
				Object.keys(updatedJob).forEach((key) => {
					const typedKey = key as keyof Job;
					const newValue = updatedJob[typedKey];
					const oldValue = currentJob[typedKey];
					if (newValue !== oldValue) {
						setValue(typedKey, newValue as any);
					}
				});

				// Extract job fields from uploaded boundary if boundary changed
				const newBoundary = updatedJob.files?.boundaryGeoJSON as FeatureCollection | undefined;
				const oldBoundary = currentJob.files?.boundaryGeoJSON as FeatureCollection | undefined;
				if (newBoundary && newBoundary !== oldBoundary && propertyMappings) {
					const extracted = extractJobFieldsFromBoundary(newBoundary, propertyMappings);
					for (const [key, value] of Object.entries(extracted)) {
						const typedKey = key as keyof Job;
						setValue(typedKey, value as any);
					}
				}
			}
		};

		const validateJobsFn = (jobsToValidate: Job[]) => {
			// Update displayed errors on blur for immediate feedback
			// But changes are NOT committed to parent until "Save Changes" is clicked
			const errors = validateJobs(jobsToValidate, {
				...validationContext,
				job: jobsToValidate[0],
			});
			setLocalValidationErrors(errors);
			return errors;
		};

		return {
			props: {
				allDeals,
				selectedCompany: company,
				salespersons,
				branches,
				growers,
				lastCropOptions,
				nextCropOptions,
			},
			jobs: [{ ...currentJob }],
			setJobs,
			validateJobs: validateJobsFn,
			validationContext,
			newSalespersons,
			setNewSalespersons,
			isNewSalesperson,
			findNewSalesperson,
			readySelectionState,
			setReadySelectionState,
			gridSizes,
			depths,
			labPackages,
			micros,
			findDeal,
		} as unknown as ColumnContext;
	}, [
		currentJob,
		setValue,
		validationContext,
		allDeals,
		company,
		salespersons,
		branches,
		growers,
		lastCropOptions,
		nextCropOptions,
		newSalespersons,
		readySelectionState,
		gridSizes,
		depths,
		labPackages,
		micros,
		propertyMappings,
	]);

	// Helper to check field visibility
	const isVisible = useCallback(
		(columnId: ImportTableColumn) => isFieldVisible(columnId, validationContext),
		[validationContext],
	);

	return {
		currentJob,
		localValidationErrors,
		gridSizes,
		depths,
		labPackages,
		micros,
		columnContext,
		isVisible,
		handleSave,
		showMoreFields,
		toggleShowMoreFields,
	};
}
